/* eslint-disable @typescript-eslint/no-explicit-any */
import { FormikProps } from "formik";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { PartnerPageLayout } from "../../components/PartnerPageLayout";
import { FormSectionHeader, getFormSectionStatus } from "../../components/FormSectionHeader";
import { PageLoadingSpinner } from "../../components/PageLoadingSpinner";
import { ScreenReaderHeading } from "../../components/ScreenReaderHeading";
import { SubFormNavigationProps } from "../../components/SubForm";
import { UnsavedChangesModal } from "../../components/UnsavedChangesModal";
import { xboxOnboardSiteLocationPrefix } from "../../constants";
import { areFormValuesEqual } from "../../helpers/areFormValuesEqual";
import {
  isPitchAboutYourGameValid,
  isPitchAboutYourselfValid,
  isPitchAskForXboxValid,
  isPitchInformationPublisherDeveloperValid,
} from "../../helpers/formValidation";
import {
  PartnerAccessLevel,
  PartnerAccountRequirement,
  usePartnerRouteGuard,
} from "../../helpers/usePartnerRouteGuard";
import { useUnsavedChangesGuard } from "../../helpers/useUnsavedChangesGuard";
import { CrmPitchConceptStatus } from "../../models/crmStatus";
import { Programs } from "../../models/programsConfig";
import { pitchPageActions } from "../../redux/actions/pitchPageActions";
import { programsActions } from "../../redux/actions/programsActions";
import { statusActions } from "../../redux/actions/statusActions";
import {
  getPitchPageData,
  getPitchPageError,
  getPitchPageIsLoading,
  getPitchPageIsSubmitting,
} from "../../redux/selectors/pitchPageSelectors";
import { getIsSavingForm } from "../../redux/selectors/statusSelectors";
import { useStrings } from "../../localization/localizationHelpers";
import AboutYourGameSubForm from "../../subForms/pitchInformation/AboutYourGameSubForm/AboutYourGameSubForm";
import AboutYourselfSubForm from "../../subForms/pitchInformation/AboutYourself/AboutYourselfSubForm";
import AskForXboxSubForm from "../../subForms/pitchInformation/AskForXboxSubForm/AskForXboxSubForm";
import {
  PitchInformationFormId,
  PitchInformationSubFormsId,
  PitchInformationSubFormsIdsOrder,
} from "../../subForms/pitchInformation/PitchInformationPageConstants";
import PublisherDeveloperSubForm from "../../subForms/pitchInformation/PublisherDeveloperSubForm/PublisherDeveloperSubForm";
import * as styles from "./PitchPage.module.css";

const pitchStatusStyleMap: Record<string, string> = {
  [CrmPitchConceptStatus.Unset]: styles.statusDraft,
  [CrmPitchConceptStatus.Submitted]: styles.statusSubmitted,
  [CrmPitchConceptStatus.Reviewed]: styles.statusReviewed,
};

interface RouteParams {
  id: string;
}

const PitchPage: React.FC = () => {
  const isPageReady = usePartnerRouteGuard(
    PartnerAccessLevel.NdaSigned,
    PartnerAccountRequirement.NonAaa
  );
  const { id } = useParams<RouteParams>();
  const dispatch = useDispatch();
  const history = useHistory();
  const formRefsMap = useRef<Record<string, React.RefObject<FormikProps<any>>>>({});
  const latestValuesRef = useRef<Record<string, Record<string, unknown>>>({});
  const initialValuesRef = useRef<Record<string, Record<string, unknown>>>({});
  const hasUserEditsRef = useRef(false);

  const data = useSelector(getPitchPageData);
  const isLoading = useSelector(getPitchPageIsLoading);
  const isSaving = useSelector(getIsSavingForm);
  const isSubmitting = useSelector(getPitchPageIsSubmitting);
  const error = useSelector(getPitchPageError);
  const [
    draftLabel,
    submittedLabel,
    reviewedLabel,
    publisherDeveloperInformationLabel,
    aboutYourGameLabel,
    askForXboxLabel,
    aboutYourselfLabel,
    pitchBreadcrumbLabel,
    pitchProgramBreadcrumbLabel,
    cancelLabel,
    saveAndCloseLabel,
    savingLabel,
    submitLabel,
    noDataLabel,
    newPitchLabel,
  ] = useStrings(
    "draft",
    "submitted",
    "reviewed",
    "pitchPublisherDeveloperInformationSectionTitle",
    "pitchInformationAboutYourGameTitle",
    "pitchInformationAskForXboxTitle",
    "pitchInformationAboutYourselfTitle",
    "pitchBreadcrumbLabel",
    "pitchProgramBreadcrumbLabel",
    "cancelButton",
    "saveAndCloseButton",
    "savingButton",
    "xboxOnboardSubmitButton",
    "pitchReadOnlyNoData",
    "pitchNewPitchDefaultTitle"
  );

  const [openSections, setOpenSections] = useState<Set<number>>(new Set());
  const [startedSections, setStartedSections] = useState<Set<string>>(new Set());
  const [isSaveAndExitPending, setIsSaveAndExitPending] = useState(false);
  const [sectionValidity, setSectionValidity] = useState<Record<string, boolean>>({});

  const validators = useMemo(
    () => ({
      [PitchInformationSubFormsId.PublisherDeveloperInformation]:
        isPitchInformationPublisherDeveloperValid,
      [PitchInformationSubFormsId.AboutYourGame]: isPitchAboutYourGameValid,
      [PitchInformationSubFormsId.AskForXbox]: isPitchAskForXboxValid,
      [PitchInformationSubFormsId.AboutYourself]: isPitchAboutYourselfValid,
    }),
    []
  );

  const pitchStatusDisplayNames = useMemo<Record<string, string>>(
    () => ({
      [CrmPitchConceptStatus.Unset]: draftLabel,
      [CrmPitchConceptStatus.Submitted]: submittedLabel,
      [CrmPitchConceptStatus.Reviewed]: reviewedLabel,
    }),
    [draftLabel, reviewedLabel, submittedLabel]
  );

  const sectionLabels = useMemo<Record<string, string>>(
    () => ({
      [PitchInformationSubFormsId.PublisherDeveloperInformation]:
        publisherDeveloperInformationLabel,
      [PitchInformationSubFormsId.AboutYourGame]: aboutYourGameLabel,
      [PitchInformationSubFormsId.AskForXbox]: askForXboxLabel,
      [PitchInformationSubFormsId.AboutYourself]: aboutYourselfLabel,
    }),
    [aboutYourGameLabel, aboutYourselfLabel, askForXboxLabel, publisherDeveloperInformationLabel]
  );

  const program = Programs.Pitch;
  const formId = PitchInformationFormId;

  useEffect(() => {
    if (!isPageReady) {
      return;
    }

    dispatch(statusActions.updateCurrentProgram(program));
  }, [dispatch, isPageReady, program]);

  useEffect(() => {
    if (!isPageReady || !id) {
      return;
    }
    dispatch(pitchPageActions.fetchPitchConcept.start(id));
  }, [dispatch, id, isPageReady]);

  const isDraft = data?.isDraft ?? true;
  const conceptState = data?.state;
  const formData = data?.formData as Record<string, Record<string, unknown>> | null | undefined;

  const isReadOnly =
    data?.canEdit === false ||
    conceptState?.status === CrmPitchConceptStatus.Submitted ||
    conceptState?.status === CrmPitchConceptStatus.Reviewed;
  const canSubmit = data?.canSubmit === true;

  const captureInitialValues = useCallback((subFormId: string, formik: FormikProps<any>) => {
    if (!initialValuesRef.current[subFormId]) {
      initialValuesRef.current[subFormId] = formik.initialValues;
    }
  }, []);

  const hasUnsavedChanges = useCallback(() => {
    if (isReadOnly) {
      return false;
    }
    if (hasUserEditsRef.current) {
      return true;
    }

    return Object.entries(formRefsMap.current).some(([subFormId, ref]) => {
      const formik = ref.current;
      if (formik) {
        captureInitialValues(subFormId, formik);
        return !areFormValuesEqual(formik.values, initialValuesRef.current[subFormId]);
      }

      const latestValues = latestValuesRef.current[subFormId];
      const initialValues = initialValuesRef.current[subFormId];
      return !!latestValues && !!initialValues && !areFormValuesEqual(latestValues, initialValues);
    });
  }, [captureInitialValues, isReadOnly]);

  const { isNavigationBlocked, allowNextNavigation, leavePage, stayOnPage } =
    useUnsavedChangesGuard(hasUnsavedChanges, !isReadOnly);

  const markAsEdited = useCallback(() => {
    hasUserEditsRef.current = true;
  }, []);

  const status = conceptState?.status ?? CrmPitchConceptStatus.Unset;
  const statusLabel = pitchStatusDisplayNames[status] ?? draftLabel;
  const statusClass = pitchStatusStyleMap[status] ?? styles.statusDraft;

  // Reset per-section UI state when switching to a different pitch concept so a
  // previously-opened concept's started/open sections never leak into another
  // one (e.g. navigating from a started concept straight into a brand-new one).
  useEffect(() => {
    setStartedSections(new Set());
    setOpenSections(new Set());
    formRefsMap.current = {};
    latestValuesRef.current = {};
    initialValuesRef.current = {};
    hasUserEditsRef.current = false;
  }, [id]);

  // Initialize startedSections from CRM data (sections previously visited).
  // Gate on the loaded data belonging to the current route id: on the first
  // render after navigation, redux may still hold the previously-opened
  // concept's formData (with isStarted set), which must not seed this concept.
  useEffect(() => {
    if (!formData || data?.pitchConceptId !== id) {
      return;
    }
    const alreadyStarted = new Set<string>();
    for (const sfId of PitchInformationSubFormsIdsOrder) {
      const sectionData = formData[sfId];
      if (sectionData?.isStarted) {
        alreadyStarted.add(sfId);
      }
    }
    if (alreadyStarted.size > 0) {
      setStartedSections(prev => {
        const merged = new Set(prev);
        for (const sfId of alreadyStarted) {
          merged.add(sfId);
        }
        return merged;
      });
    }
  }, [formData, data?.pitchConceptId, id]);

  // Seed programs Redux store with loaded CRM data so SubForm can read it
  useEffect(() => {
    if (!formData || data?.pitchConceptId !== id) {
      return;
    }
    for (const sfId of PitchInformationSubFormsIdsOrder) {
      const sectionData = formData[sfId];
      if (sectionData) {
        dispatch(
          programsActions.updateSubFormData(program, {
            formId,
            subFormId: sfId,
            subForm: sectionData as any,
          })
        );
      }
    }
  }, [formData, data?.pitchConceptId, id, dispatch, program, formId]);

  // Helper to get the best available data for a subform section
  const getSectionData = useCallback(
    (subFormId: string) => {
      const ref = formRefsMap.current[subFormId];
      return ref?.current?.values ?? latestValuesRef.current[subFormId] ?? formData?.[subFormId];
    },
    [formData]
  );

  // Validate all sections when data loads or changes
  useEffect(() => {
    if (!formData && startedSections.size === 0) {
      return;
    }

    const runValidation = async () => {
      const results: Record<string, boolean> = {};
      for (const subFormId of PitchInformationSubFormsIdsOrder) {
        const sectionData = getSectionData(subFormId);
        const validator = validators[subFormId as keyof typeof validators];
        if (validator && sectionData) {
          try {
            results[subFormId] = await validator(sectionData as any);
          } catch {
            results[subFormId] = false;
          }
        } else {
          results[subFormId] = false;
        }
      }
      setSectionValidity(results);
    };

    runValidation();
  }, [formData, getSectionData, startedSections, validators]);

  // Re-validate on an interval to pick up live Formik edits
  useEffect(() => {
    const interval = setInterval(async () => {
      const results: Record<string, boolean> = {};
      for (const subFormId of PitchInformationSubFormsIdsOrder) {
        const ref = formRefsMap.current[subFormId];
        if (ref?.current?.values) {
          captureInitialValues(subFormId, ref.current);
          latestValuesRef.current[subFormId] = ref.current.values;
        }
        const sectionData = getSectionData(subFormId);
        const validator = validators[subFormId as keyof typeof validators];
        if (validator && sectionData) {
          try {
            results[subFormId] = await validator(sectionData as any);
          } catch {
            results[subFormId] = false;
          }
        } else {
          results[subFormId] = false;
        }
      }
      setSectionValidity(results);
    }, 2000);
    return () => clearInterval(interval);
  }, [captureInitialValues, getSectionData, validators]);

  const getFormRef = useCallback((subFormId: string): React.RefObject<FormikProps<any>> => {
    if (!formRefsMap.current[subFormId]) {
      formRefsMap.current[subFormId] = React.createRef<FormikProps<any>>();
    }
    return formRefsMap.current[subFormId];
  }, []);

  const onSaveFormToCrm = useCallback((_subFormId: string) => {
    // no-op — save is triggered by handleSave
  }, []);

  const onForwardNavigation = useCallback(() => {
    // No-op in accordion mode
  }, []);

  const onCancel = useCallback(() => {
    history.push(`${xboxOnboardSiteLocationPrefix}/pitch`);
  }, [history]);

  const getSubFormProps = useCallback(
    (subFormId: string): SubFormNavigationProps => ({
      formRef: getFormRef(subFormId),
      onCancel,
      onBackNavigation: undefined,
      onForwardNavigation,
      onSaveFormToCrm,
      skipScrollToTop: true,
      hideNavigation: true,
    }),
    [onCancel, onForwardNavigation, onSaveFormToCrm, getFormRef]
  );

  const buildSavePayload = useCallback(() => {
    const payload: Record<string, unknown> = {};

    PitchInformationSubFormsIdsOrder.forEach((subFormId, index) => {
      // Only sections the user actually opened count as started. Sections that
      // were never opened are intentionally omitted from the payload so the CRM
      // mappers (which mark a section "started" whenever its data is present)
      // never flag them as started.
      const wasStarted = startedSections.has(subFormId) || openSections.has(index);
      if (!wasStarted) {
        return;
      }

      const ref = formRefsMap.current[subFormId];
      let sfData: Record<string, unknown> | undefined;
      if (openSections.has(index) && ref?.current?.values) {
        sfData = ref.current.values;
      } else if (latestValuesRef.current[subFormId]) {
        sfData = latestValuesRef.current[subFormId];
      } else if (formData?.[subFormId]) {
        sfData = formData[subFormId] as Record<string, unknown>;
      }

      if (sfData) {
        payload[subFormId] = { ...sfData, isStarted: true };
      }
    });

    // Update programs Redux store for consistency
    for (const [subFormId, sfData] of Object.entries(payload)) {
      dispatch(
        programsActions.updateSubFormData(program, {
          formId,
          subFormId,
          subForm: sfData as any,
        })
      );
    }

    return payload;
  }, [dispatch, formData, startedSections, openSections, program, formId]);

  const handleSaveAndClose = useCallback(() => {
    const payload = buildSavePayload();
    dispatch(pitchPageActions.savePitchConcept.start(id, isDraft, payload));
    setIsSaveAndExitPending(true);
  }, [dispatch, id, isDraft, buildSavePayload]);

  const handleCancel = useCallback(() => {
    history.push(`${xboxOnboardSiteLocationPrefix}/pitch`);
  }, [history]);

  // Navigate to pitch list after save completes
  const saveStartedRef = useRef(false);
  useEffect(() => {
    if (isSaveAndExitPending && isSaving) {
      saveStartedRef.current = true;
    }
    if (isSaveAndExitPending && saveStartedRef.current && !isSaving) {
      setIsSaveAndExitPending(false);
      saveStartedRef.current = false;
      if (error) {
        return;
      }
      allowNextNavigation();
      history.push(`${xboxOnboardSiteLocationPrefix}/pitch`);
    }
  }, [allowNextNavigation, error, isSaveAndExitPending, isSaving, history]);

  const handleSubmit = useCallback(() => {
    const payload = buildSavePayload();
    dispatch(pitchPageActions.submitPitchConcept.start(id, isDraft, payload));
  }, [dispatch, id, isDraft, buildSavePayload]);

  const allSectionsValid = PitchInformationSubFormsIdsOrder.every(
    sfId => sectionValidity[sfId] === true
  );

  const handleSectionClick = (index: number) => {
    const subFormId = PitchInformationSubFormsIdsOrder[index];
    const isCurrentlyOpen = openSections.has(index);

    if (isCurrentlyOpen) {
      // Persist unsaved values to Redux before closing
      const ref = formRefsMap.current[subFormId];
      if (ref?.current?.values) {
        captureInitialValues(subFormId, ref.current);
        latestValuesRef.current[subFormId] = ref.current.values;
        dispatch(
          programsActions.updateSubFormData(program, {
            formId,
            subFormId,
            subForm: { ...ref.current.values, isStarted: true },
          })
        );
      }
      setOpenSections(prev => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    } else {
      // Mark section as started when opened for the first time
      if (!startedSections.has(subFormId)) {
        setStartedSections(prev => new Set(prev).add(subFormId));
        const existingData = formData?.[subFormId] ?? {};
        dispatch(
          programsActions.updateSubFormData(program, {
            formId,
            subFormId,
            subForm: { ...existingData, isStarted: true } as any,
          })
        );
      }
      setOpenSections(prev => {
        const next = new Set(prev);
        next.add(index);
        return next;
      });
    }
  };

  const renderSubForm = (subFormId: string, props: SubFormNavigationProps) => {
    switch (subFormId) {
      case PitchInformationSubFormsId.PublisherDeveloperInformation:
        return <PublisherDeveloperSubForm {...props} />;
      case PitchInformationSubFormsId.AboutYourGame:
        return <AboutYourGameSubForm {...props} />;
      case PitchInformationSubFormsId.AskForXbox:
        return <AskForXboxSubForm {...props} />;
      case PitchInformationSubFormsId.AboutYourself:
        return <AboutYourselfSubForm {...props} />;
      default:
        return null;
    }
  };

  const renderReadOnlySection = (subFormId: string) => {
    const sectionData = formData?.[subFormId];
    if (!sectionData) {
      return <p className={styles.readOnlyNoData}>{noDataLabel}</p>;
    }

    return (
      <div className={styles.readOnlySection}>
        {Object.entries(sectionData).map(([key, value]) => {
          if (key === "isStarted" || value === null || value === undefined || value === "") {
            return null;
          }
          const displayValue = Array.isArray(value) ? value.join(", ") : String(value);
          const label = key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase());
          return (
            <div key={key} className={styles.readOnlyField}>
              <div className={styles.readOnlyLabel}>{label}</div>
              <div className={styles.readOnlyValue}>{displayValue}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const gameTitle =
    (formData?.aboutYourGame as { gameTitle?: string } | undefined)?.gameTitle || newPitchLabel;

  if (!isPageReady) {
    return null;
  }

  if (isLoading) {
    return (
      <PartnerPageLayout
        breadcrumbLabel={pitchBreadcrumbLabel}
        breadcrumbSteps={[
          { label: pitchProgramBreadcrumbLabel, to: `${xboxOnboardSiteLocationPrefix}/pitch` },
        ]}
      >
        <PageLoadingSpinner />
      </PartnerPageLayout>
    );
  }

  return (
    <>
      <PartnerPageLayout
        breadcrumbLabel={pitchBreadcrumbLabel}
        breadcrumbSteps={[
          { label: pitchProgramBreadcrumbLabel, to: `${xboxOnboardSiteLocationPrefix}/pitch` },
        ]}
      >
        <ScreenReaderHeading>{gameTitle}</ScreenReaderHeading>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {isDraft ? (
              <span className={styles.draftBadge}>{draftLabel}</span>
            ) : (
              <span className={`${styles.statusBadge} ${statusClass}`}>{statusLabel}</span>
            )}
          </div>
          <div className={styles.headerRight}>
            {!isReadOnly &&
              (() => {
                const isSaveInProgress = isSaving || isSaveAndExitPending;
                const isBusy = isSaveInProgress || isSubmitting;
                return (
                  <>
                    <button
                      className={styles.cancelButton}
                      onClick={handleCancel}
                      disabled={isBusy}
                    >
                      {cancelLabel}
                    </button>
                    <button
                      className={styles.saveButton}
                      onClick={handleSaveAndClose}
                      disabled={isLoading || isBusy}
                    >
                      {isSaveInProgress ? savingLabel : saveAndCloseLabel}
                    </button>
                    <button
                      className={styles.submitButton}
                      onClick={handleSubmit}
                      disabled={!canSubmit || isLoading || isBusy || !allSectionsValid}
                    >
                      {submitLabel}
                    </button>
                  </>
                );
              })()}
          </div>
        </div>

        {error && (
          <p className={styles.errorMessage} role="alert">
            {error}
          </p>
        )}

        <div className={styles.body} onChangeCapture={markAsEdited} onInputCapture={markAsEdited}>
          <div className={styles.mainColumn}>
            {isReadOnly
              ? PitchInformationSubFormsIdsOrder.map((subFormId, index) => {
                  const isOpen = openSections.has(index);
                  const label = sectionLabels[subFormId] ?? subFormId;
                  const headerId = `pitch-header-${subFormId}`;
                  const panelId = `pitch-panel-${subFormId}`;

                  return (
                    <div key={subFormId} className={styles.accordionSection}>
                      <button
                        className={`${styles.accordionHeader} ${
                          isOpen ? styles.accordionHeaderOpen : ""
                        }`}
                        onClick={() => handleSectionClick(index)}
                        type="button"
                        id={headerId}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                      >
                        <span className={styles.accordionTitle}>{label}</span>
                        <span
                          aria-hidden="true"
                          className={`${styles.accordionChevron} ${
                            isOpen ? styles.accordionChevronOpen : ""
                          }`}
                        >
                          &#8250;
                        </span>
                      </button>
                      {isOpen && (
                        <div
                          id={panelId}
                          className={styles.accordionContent}
                          role="region"
                          aria-labelledby={headerId}
                        >
                          {renderReadOnlySection(subFormId)}
                        </div>
                      )}
                    </div>
                  );
                })
              : PitchInformationSubFormsIdsOrder.map((subFormId, index) => {
                  const isOpen = openSections.has(index);
                  const label = sectionLabels[subFormId] ?? subFormId;
                  const headerId = `pitch-header-${subFormId}`;
                  const panelId = `pitch-panel-${subFormId}`;

                  return (
                    <div key={subFormId} className={styles.accordionSection}>
                      <button
                        className={`${styles.accordionHeader} ${
                          isOpen ? styles.accordionHeaderOpen : ""
                        }`}
                        onClick={() => handleSectionClick(index)}
                        type="button"
                        id={headerId}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                      >
                        <FormSectionHeader
                          status={getFormSectionStatus(
                            startedSections.has(subFormId),
                            sectionValidity[subFormId] === true
                          )}
                          title={label}
                        />
                        <span
                          aria-hidden="true"
                          className={`${styles.accordionChevron} ${
                            isOpen ? styles.accordionChevronOpen : ""
                          }`}
                        >
                          &#8250;
                        </span>
                      </button>
                      {isOpen && (
                        <div
                          id={panelId}
                          className={styles.accordionContent}
                          role="region"
                          aria-labelledby={headerId}
                        >
                          {renderSubForm(subFormId, getSubFormProps(subFormId))}
                        </div>
                      )}
                    </div>
                  );
                })}
          </div>

          <aside className={styles.sidePanel}>
            <div className={styles.gameInfoCard}>
              <h3 className={styles.gameInfoTitle}>{gameTitle}</h3>
            </div>
          </aside>
        </div>
      </PartnerPageLayout>
      <UnsavedChangesModal isOpen={isNavigationBlocked} onLeave={leavePage} onStay={stayOnPage} />
    </>
  );
};

export default PitchPage;
